<template>
  <div class="pl-page">
    <div v-if="err" class="pl-err">{{ err }}</div>
    <template v-else-if="album">
      <div v-if="showCover" class="pl-cover">
        <div v-if="album.coverImg" class="pl-cover__bg" :style="coverStyle" />
        <div class="pl-cover__meta">
          <p v-if="album.slogan" class="pl-cover__slogan">{{ album.slogan }}</p>
          <h1 class="pl-cover__title">{{ album.title }}</h1>
          <p v-if="coverSub" class="pl-cover__sub">{{ coverSub }}</p>
        </div>
        <button class="pl-btn" @click="enterAlbum">进入相册</button>
      </div>
      <div v-else class="pl-album">
        <div class="pl-banner" :style="bannerStyle">
          <div class="pl-banner__veil" />
        </div>
        <div class="pl-head">
          <div class="pl-title-row">
            <h2>{{ album.title }}</h2>
            <span v-if="album.status === 1" class="pl-live"><i></i>LIVE</span>
            <span v-else-if="album.status === 2" class="pl-ended">已结束</span>
          </div>
          <p class="pl-meta">
            <span>{{ album.viewCount || 0 }} 人次浏览</span>
            <span v-if="metaDate" class="pl-meta__sep">·</span>
            <span v-if="metaDate">{{ metaDate }}</span>
            <span v-if="album.city" class="pl-meta__sep">·</span>
            <span v-if="album.city">{{ album.city }}</span>
          </p>
          <div class="pl-tabs">
            <div class="pl-tabs__group">
              <span class="pl-tab" :class="{ on: tab === 'live' }" @click="switchTab('live')">照片直播</span>
              <span class="pl-tab" :class="{ on: tab === 'hot' }" @click="switchTab('hot')">热门</span>
            </div>
            <span class="pl-order" @click="toggleOrder">{{ order === 'asc' ? '正序' : '倒序' }}</span>
          </div>
        </div>
        <div v-if="newTipCount > 0" class="pl-newtip" @click="applyNewPhotos">有 {{ newTipCount }} 张新照片</div>
        <div class="pl-waterfall" ref="waterfall" @scroll.passive="onScroll">
          <div class="pl-col">
            <div
              v-for="p in leftCol"
              :key="'L' + p.id"
              class="pl-card"
              :class="{ enter: p._enter, selected: selectMode && selectedIds[p.id] }"
              :data-id="p.id"
              @click="onCardClick(p)"
            >
              <div class="pl-ph" :class="{ ready: loadedMap[p.id] }" :style="{ paddingBottom: ratio(p) + '%' }">
                <div v-if="!loadedMap[p.id]" class="pl-ph__shine" />
                <img
                  v-if="visibleMap[p.id]"
                  :src="p.thumbUrl"
                  alt=""
                  :class="{ show: loadedMap[p.id] }"
                  @load="markLoaded(p.id)"
                />
                <i v-if="selectMode && selectedIds[p.id]" class="pl-check">✓</i>
              </div>
            </div>
          </div>
          <div class="pl-col">
            <div
              v-for="p in rightCol"
              :key="'R' + p.id"
              class="pl-card"
              :class="{ enter: p._enter, selected: selectMode && selectedIds[p.id] }"
              :data-id="p.id"
              @click="onCardClick(p)"
            >
              <div class="pl-ph" :class="{ ready: loadedMap[p.id] }" :style="{ paddingBottom: ratio(p) + '%' }">
                <div v-if="!loadedMap[p.id]" class="pl-ph__shine" />
                <img
                  v-if="visibleMap[p.id]"
                  :src="p.thumbUrl"
                  alt=""
                  :class="{ show: loadedMap[p.id] }"
                  @load="markLoaded(p.id)"
                />
                <i v-if="selectMode && selectedIds[p.id]" class="pl-check">✓</i>
              </div>
            </div>
          </div>
          <div v-if="!photos.length" class="pl-empty">
            <div class="pl-sk">
              <div class="pl-sk__col">
                <div class="pl-sk__card" style="padding-top: 118%"></div>
                <div class="pl-sk__card" style="padding-top: 88%"></div>
                <div class="pl-sk__card" style="padding-top: 104%"></div>
              </div>
              <div class="pl-sk__col">
                <div class="pl-sk__card" style="padding-top: 92%"></div>
                <div class="pl-sk__card" style="padding-top: 124%"></div>
                <div class="pl-sk__card" style="padding-top: 86%"></div>
              </div>
            </div>
            <p class="pl-sk__hint"><i></i>摄影师正在赶图，照片马上上墙</p>
          </div>
        </div>
        <div v-if="loadingMore && photos.length" class="pl-loading pl-loading--more">加载中…</div>

        <div class="pl-bar">
          <button type="button" @click="goMine">我的</button>
          <button type="button" @click="shareOpen = true">分享</button>
          <button type="button" :class="{ on: selectMode }" @click="toggleSelect">
            {{ selectMode ? '取消' : '多选' }}
          </button>
          <button
            v-if="album.allowCollage === 1"
            type="button"
            :disabled="collageBusy || (selectMode && (!selectedCount || selectedCount > 9))"
            @click="doCollage"
          >
            {{ collageBusy ? '拼图中…' : '拼图' }}
          </button>
          <button
            v-if="album.allowDownload === 1"
            type="button"
            :disabled="selectMode && !selectedCount"
            @click="downloadSelected"
          >
            下载{{ selectMode && selectedCount ? '(' + selectedCount + ')' : '' }}
          </button>
        </div>
        <div v-if="album.allowUserUpload === 1 && album.status === 1" class="pl-fab" @click="pickUserUpload">
          {{ userUploading ? '…' : '上传' }}
        </div>
        <input ref="userFile" type="file" accept="image/*" multiple hidden @change="onUserFiles" />
      </div>
    </template>
    <div v-else class="pl-loading">加载中…</div>

    <!-- 大图画廊 -->
    <div v-if="galleryOpen" class="pl-gallery" @touchstart.passive="onGalTouchStart" @touchend="onGalTouchEnd">
      <button class="pl-gallery__close" type="button" @click="closeGallery">×</button>
      <img :src="galleryUrl" alt="" class="pl-gallery__img" />
      <div class="pl-gallery__actions">
        <span>{{ galleryIndex + 1 }} / {{ photos.length }}</span>
        <button v-if="album && album.allowDownload === 1" type="button" @click="downloadOne(photos[galleryIndex], 'display')">预览图</button>
        <button v-if="album && album.allowDownload === 1" type="button" @click="downloadOne(photos[galleryIndex], 'original')">原图</button>
      </div>
      <p class="pl-gallery__hint">左右滑切换 · 长按图片可保存</p>
    </div>

    <!-- 分享 -->
    <div v-if="shareOpen" class="pl-share-mask" @click.self="shareOpen = false">
      <div class="pl-share">
        <h3>分享相册</h3>
        <button type="button" @click="copyLink">复制链接</button>
        <button type="button" :disabled="posterBusy" @click="makePoster">{{ posterBusy ? '生成中…' : '生成海报' }}</button>
        <img v-if="posterUrl" :src="posterUrl" class="pl-poster" alt="海报" />
        <p v-if="posterUrl" class="pl-share-tip">长按海报保存</p>
        <button type="button" class="pl-share-cancel" @click="shareOpen = false">关闭</button>
      </div>
    </div>

    <!-- 拼图结果 -->
    <div v-if="collageUrl" class="pl-share-mask" @click.self="collageUrl = ''">
      <div class="pl-share">
        <h3>拼图长图</h3>
        <img :src="collageUrl" class="pl-poster" alt="拼图" />
        <p class="pl-share-tip">长按图片保存到相册</p>
        <button type="button" @click="triggerDownload(collageUrl, 'collage')">下载拼图</button>
        <button type="button" class="pl-share-cancel" @click="collageUrl = ''">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getPhotoLiveAlbum,
  getPhotoLivePhotos,
  postPhotoLiveView,
  photoLiveUserUpload,
  photoLiveUserUploadFile,
  photoLiveDownload,
  getPhotoLiveUid
} from '@/api/photoLive'

function pickUrls(res) {
  if (!res) return {}
  const originalUrl = res.originalUrl || res.url || ''
  return {
    originalUrl,
    displayUrl: res.displayUrl || originalUrl,
    thumbUrl: res.thumbUrl || originalUrl,
    width: res.width,
    height: res.height
  }
}

export default {
  name: 'PhotoLiveAlbum',
  data() {
    return {
      albumCode: '',
      album: null,
      err: '',
      showCover: true,
      tab: 'live',
      order: 'desc',
      photos: [],
      page: 0,
      finished: false,
      loadingMore: false,
      visibleMap: {},
      loadedMap: {},
      pollTimer: null,
      maxId: 0,
      pendingNew: [],
      newTipCount: 0,
      scrollTimer: null,
      galleryOpen: false,
      galleryIndex: 0,
      touchX: 0,
      selectMode: false,
      selectedIds: {},
      shareOpen: false,
      posterUrl: '',
      posterBusy: false,
      userUploading: false,
      collageBusy: false,
      collageUrl: '',
      viewerUid: ''
    }
  },
  computed: {
    coverStyle() {
      const img = (this.album && this.album.coverImg) || ''
      return img ? { backgroundImage: 'url(' + img + ')' } : {}
    },
    bannerStyle() {
      const img = (this.album && (this.album.bannerImg || this.album.coverImg)) || ''
      return img ? { backgroundImage: 'url(' + img + ')' } : {}
    },
    metaDate() {
      const d = (this.album && this.album.eventDate) || ''
      const m = String(d).match(/(\d{4})-(\d{1,2})-(\d{1,2})/)
      return m ? m[1] + '.' + ('0' + m[2]).slice(-2) + '.' + ('0' + m[3]).slice(-2) : String(d)
    },
    coverSub() {
      if (!this.album) return ''
      return [this.metaDate, this.album.city || this.album.locationText].filter(Boolean).join(' · ')
    },
    leftCol() {
      return this.photos.filter((_, i) => i % 2 === 0)
    },
    rightCol() {
      return this.photos.filter((_, i) => i % 2 === 1)
    },
    galleryUrl() {
      const p = this.photos[this.galleryIndex]
      return p ? (p.displayUrl || p.thumbUrl) : ''
    },
    selectedCount() {
      return Object.keys(this.selectedIds).filter(k => this.selectedIds[k]).length
    },
    shareLink() {
      if (typeof window === 'undefined') return ''
      return window.location.origin + '/#/photo-live/' + encodeURIComponent(this.albumCode)
    }
  },
  created() {
    this.albumCode = this.$route.params.albumCode || this.$route.query.albumCode || ''
    this.viewerUid = getPhotoLiveUid()
    if (!this.albumCode) {
      this.err = '缺少相册编码'
      return
    }
    this.bootstrap()
  },
  beforeDestroy() {
    this.stopPoll()
    if (this.scrollTimer) clearTimeout(this.scrollTimer)
  },
  methods: {
    markLoaded(id) {
      if (!this.loadedMap[id]) this.$set(this.loadedMap, id, true)
    },
    ratio(p) {
      if (p.width && p.height) return Math.min(160, Math.max(60, (p.height / p.width) * 100))
      return 100
    },
    async bootstrap() {
      try {
        const album = await getPhotoLiveAlbum(this.albumCode)
        this.album = album
        this.order = album.sortDefault === 'asc' ? 'asc' : 'desc'
        this.showCover = album.showCover !== 0
        // 从「我的下载」等内页返回时跳过封面，直接进相册
        if (String(this.$route.query.skipCover) === '1') this.showCover = false
        if (!this.showCover) this.enterAlbum()
      } catch (e) {
        this.err = (e && (e.msg || e.message)) || '加载失败'
      }
    },
    enterAlbum() {
      this.showCover = false
      this.page = 0
      this.photos = []
      this.finished = false
      this.loadedMap = {}
      this.loadMore().then(() => {
        postPhotoLiveView(this.albumCode).catch(() => {})
        this.startPoll()
        this.$nextTick(() => this.scheduleVisibleLoad())
      })
    },
    switchTab(t) {
      if (this.tab === t) return
      this.tab = t
      this.reloadList()
    },
    toggleOrder() {
      this.order = this.order === 'asc' ? 'desc' : 'asc'
      this.reloadList()
    },
    reloadList() {
      this.page = 0
      this.photos = []
      this.finished = false
      this.loadedMap = {}
      this.loadMore().then(() => this.$nextTick(() => this.scheduleVisibleLoad()))
    },
    async loadMore() {
      if (this.loadingMore || this.finished) return
      this.loadingMore = true
      try {
        const res = await getPhotoLivePhotos(this.albumCode, {
          tab: this.tab,
          order: this.order,
          page: this.page,
          size: 20
        })
        const list = (res && res.content) || []
        if (!list.length) this.finished = true
        else {
          this.photos = this.photos.concat(list)
          list.forEach(p => { if (p.id > this.maxId) this.maxId = p.id })
          this.page += 1
        }
      } catch (e) {
        // ignore
      } finally {
        this.loadingMore = false
      }
    },
    onScroll() {
      const el = this.$refs.waterfall
      if (el && el.scrollTop + el.clientHeight > el.scrollHeight - 200) this.loadMore()
      if (this.scrollTimer) clearTimeout(this.scrollTimer)
      this.scrollTimer = setTimeout(() => this.scheduleVisibleLoad(), 120)
    },
    scheduleVisibleLoad() {
      const root = this.$refs.waterfall
      if (!root) return
      const cards = root.querySelectorAll('.pl-card')
      const rootRect = root.getBoundingClientRect()
      const ids = []
      cards.forEach(node => {
        const r = node.getBoundingClientRect()
        const inView = r.bottom > rootRect.top - rootRect.height && r.top < rootRect.bottom + rootRect.height
        if (inView) ids.push(Number(node.getAttribute('data-id')))
      })
      ids.sort((a, b) => {
        const ia = this.photos.findIndex(p => p.id === a)
        const ib = this.photos.findIndex(p => p.id === b)
        return ia - ib
      })
      const next = { ...this.visibleMap }
      ids.forEach(id => { next[id] = true })
      this.visibleMap = next
    },
    startPoll() {
      this.stopPoll()
      this.pollTimer = setInterval(() => this.pollNew(), 20000)
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async pollNew() {
      if (this.tab !== 'live' || document.hidden) return
      try {
        const res = await getPhotoLivePhotos(this.albumCode, {
          tab: 'live',
          sinceId: this.maxId,
          size: 30
        })
        const list = (res && res.content) || []
        if (!list.length) return
        const el = this.$refs.waterfall
        const nearTop = !el || el.scrollTop < 80
        if (nearTop) this.insertNew(list)
        else {
          this.pendingNew = list
          this.newTipCount = list.length
        }
      } catch (e) { /* ignore */ }
    },
    applyNewPhotos() {
      this.insertNew(this.pendingNew)
      this.pendingNew = []
      this.newTipCount = 0
      const el = this.$refs.waterfall
      if (el) el.scrollTop = 0
    },
    insertNew(list) {
      const exist = new Set(this.photos.map(p => p.id))
      const add = list.filter(p => !exist.has(p.id)).map(p => ({ ...p, _enter: true }))
      if (!add.length) return
      this.photos = add.concat(this.photos)
      add.forEach(p => { if (p.id > this.maxId) this.maxId = p.id })
      this.$nextTick(() => {
        this.scheduleVisibleLoad()
        setTimeout(() => {
          this.photos.forEach(p => { if (p._enter) this.$set(p, '_enter', false) })
        }, 400)
      })
    },
    onCardClick(p) {
      if (this.selectMode) {
        if (this.selectedIds[p.id]) this.$delete(this.selectedIds, p.id)
        else {
          const max = this.album && this.album.allowCollage === 1 ? 20 : 20
          if (this.selectedCount >= max) {
            alert('单次最多选 ' + max + ' 张')
            return
          }
          this.$set(this.selectedIds, p.id, true)
        }
        return
      }
      this.openGallery(p)
    },
    openGallery(p) {
      const idx = this.photos.findIndex(x => x.id === p.id)
      this.galleryIndex = idx >= 0 ? idx : 0
      this.galleryOpen = true
    },
    closeGallery() {
      this.galleryOpen = false
    },
    onGalTouchStart(e) {
      this.touchX = (e.changedTouches && e.changedTouches[0] && e.changedTouches[0].clientX) || 0
    },
    onGalTouchEnd(e) {
      const x = (e.changedTouches && e.changedTouches[0] && e.changedTouches[0].clientX) || 0
      const dx = x - this.touchX
      if (Math.abs(dx) < 40) return
      if (dx < 0 && this.galleryIndex < this.photos.length - 1) this.galleryIndex += 1
      if (dx > 0 && this.galleryIndex > 0) this.galleryIndex -= 1
    },
    toggleSelect() {
      this.selectMode = !this.selectMode
      this.selectedIds = {}
    },
    goMine() {
      this.$router.push({ name: 'PhotoLiveMine', params: { albumCode: this.albumCode } })
    },
    async downloadOne(p, quality) {
      if (!p) return
      try {
        const res = await photoLiveDownload({
          albumCode: this.albumCode,
          photoId: p.id,
          quality: quality || 'display',
          uid: this.viewerUid
        })
        const item = res && res.items && res.items[0]
        if (item && item.url) this.triggerDownload(item.url, 'photo-' + p.id)
      } catch (e) {
        alert((e && (e.msg || e.message)) || '下载失败')
      }
    },
    async downloadSelected() {
      if (!this.selectMode) {
        this.selectMode = true
        return
      }
      const ids = Object.keys(this.selectedIds).filter(k => this.selectedIds[k]).map(Number)
      if (!ids.length) return
      if (ids.length > 20) {
        alert('单次最多下载 20 张')
        return
      }
      try {
        const res = await photoLiveDownload({
          albumCode: this.albumCode,
          photoIds: ids,
          quality: 'display',
          uid: this.viewerUid
        })
        const items = (res && res.items) || []
        for (const item of items) {
          if (item.url) this.triggerDownload(item.url, 'photo-' + item.photoId)
          await new Promise(r => setTimeout(r, 280))
        }
        this.selectMode = false
        this.selectedIds = {}
      } catch (e) {
        alert((e && (e.msg || e.message)) || '下载失败')
      }
    },
    async doCollage() {
      if (!this.selectMode) {
        this.selectMode = true
        alert('请先多选最多 9 张照片，再点拼图')
        return
      }
      const ids = Object.keys(this.selectedIds).filter(k => this.selectedIds[k]).map(Number)
      if (!ids.length) {
        alert('请先选择照片')
        return
      }
      if (ids.length > 9) {
        alert('拼图最多 9 张')
        return
      }
      const list = ids.map(id => this.photos.find(p => p.id === id)).filter(Boolean)
      if (!list.length) return
      this.collageBusy = true
      this.collageUrl = ''
      try {
        const imgs = []
        for (const p of list) {
          imgs.push(await this.loadImg(p.displayUrl || p.thumbUrl))
        }
        const outW = 750
        const gap = 8
        let totalH = gap
        const scaled = imgs.map(img => {
          const h = Math.round(outW * img.height / img.width)
          totalH += h + gap
          return { img, h }
        })
        const canvas = document.createElement('canvas')
        canvas.width = outW
        canvas.height = totalH
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#111'
        ctx.fillRect(0, 0, outW, totalH)
        let y = gap
        scaled.forEach(({ img, h }) => {
          ctx.drawImage(img, 0, y, outW, h)
          y += h + gap
        })
        this.collageUrl = canvas.toDataURL('image/jpeg', 0.9)
        this.selectMode = false
        this.selectedIds = {}
      } catch (e) {
        alert('拼图失败，请换图重试（需图片允许跨域）')
      } finally {
        this.collageBusy = false
      }
    },
    triggerDownload(url, name) {
      const a = document.createElement('a')
      a.href = url
      a.target = '_blank'
      a.rel = 'noopener'
      a.download = name || 'photo'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    pickUserUpload() {
      if (this.userUploading) return
      this.$refs.userFile && this.$refs.userFile.click()
    },
    async onUserFiles(e) {
      const files = Array.from((e.target && e.target.files) || [])
      if (e.target) e.target.value = ''
      if (!files.length) return
      this.userUploading = true
      let ok = 0
      let fail = 0
      for (const file of files.slice(0, 9)) {
        try {
          const fd = new FormData()
          fd.append('file', file)
          fd.append('albumCode', this.albumCode)
          const up = await photoLiveUserUploadFile(fd)
          const urls = pickUrls(up)
          if (!urls.originalUrl) throw new Error('无地址')
          await photoLiveUserUpload({
            albumCode: this.albumCode,
            originalUrl: urls.originalUrl,
            displayUrl: urls.displayUrl,
            thumbUrl: urls.thumbUrl,
            width: urls.width,
            height: urls.height,
            uploaderUid: localStorage.getItem('h5_uid') || 'h5'
          })
          ok += 1
        } catch (err) {
          fail += 1
        }
      }
      this.userUploading = false
      alert(ok ? ('已提交 ' + ok + ' 张，等待审核' + (fail ? '，失败 ' + fail : '')) : '上传失败')
    },
    copyLink() {
      const link = this.shareLink
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(() => alert('链接已复制')).catch(() => {
          prompt('复制链接', link)
        })
      } else {
        prompt('复制链接', link)
      }
    },
    async makePoster() {
      this.posterBusy = true
      this.posterUrl = ''
      try {
        const w = 750
        const h = 1200
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#f8d7e8'
        ctx.fillRect(0, 0, w, h)
        const cover = (this.album && (this.album.coverImg || this.album.bannerImg)) || ''
        if (cover) {
          const img = await this.loadImg(cover)
          const scale = Math.max(w / img.width, (h * 0.62) / img.height)
          const iw = img.width * scale
          const ih = img.height * scale
          ctx.drawImage(img, (w - iw) / 2, 0, iw, ih)
        }
        ctx.fillStyle = 'rgba(255,255,255,0.94)'
        roundRect(ctx, 48, 720, w - 96, 400, 28)
        ctx.fill()
        ctx.fillStyle = '#2b2b2b'
        ctx.font = 'bold 44px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText((this.album && this.album.title) || '照片墙', w / 2, 820)
        ctx.fillStyle = '#8a7a80'
        ctx.font = '28px sans-serif'
        ctx.fillText(this.coverSub || '扫码查看现场照片', w / 2, 880)
        ctx.fillStyle = '#b88'
        ctx.font = '24px sans-serif'
        ctx.fillText(this.shareLink.replace(/^https?:\/\//, '').slice(0, 42), w / 2, 1040)
        this.posterUrl = canvas.toDataURL('image/jpeg', 0.92)
      } catch (e) {
        alert('海报生成失败')
      } finally {
        this.posterBusy = false
      }
    },
    loadImg(src) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
      })
    }
  }
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}
</script>

<style scoped>
/* 本页 px 数值按 750 设计宽书写，构建时自动转 vw，视觉尺寸约为数值的一半 */
.pl-page { position: relative; min-height: 100vh; background: #f7f5f2; }
.pl-err, .pl-loading { padding: 120px 40px; text-align: center; color: #666; font-size: 28px; }
.pl-cover {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background-color: #f8d7e8;
}
.pl-cover__bg {
  position: absolute;
  left: 0; top: 0; width: 100%; height: 100%;
  background-size: cover;
  background-position: center;
  transform: scale(1.1);
  animation: plCoverZoom 1.8s ease-out forwards;
}
.pl-cover__meta {
  position: absolute;
  z-index: 1;
  left: 48px; right: 48px;
  top: 18%;
  text-align: center;
  color: #fff;
  text-shadow: 0 4px 24px rgba(0,0,0,.35);
}
.pl-cover__slogan { margin: 0; font-size: 30px; opacity: .92; }
.pl-cover__title { margin: 20px 0 0; font-size: 64px; font-weight: 700; }
.pl-cover__sub { margin: 20px 0 0; font-size: 28px; opacity: .88; }
@keyframes plCoverZoom {
  from { transform: scale(1.1); }
  to { transform: scale(1); }
}
.pl-btn {
  position: absolute;
  z-index: 1;
  left: 50%;
  bottom: calc(190px + env(safe-area-inset-bottom));
  transform: translateX(-50%);
  width: 84%;
  max-width: 680px;
  height: 100px;
  border: 0;
  background: linear-gradient(135deg, #cbab7d, #a97f4e);
  color: #fff;
  border-radius: 50px;
  font-size: 40px;
  font-weight: 600;
  letter-spacing: 4px;
  box-shadow: 0 12px 32px rgba(120,85,45,.32);
  animation: plBtnIn .6s ease .4s both;
}
@keyframes plBtnIn {
  from { opacity: 0; transform: translateX(-50%) translateY(24px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
.pl-album { position: relative; display: flex; flex-direction: column; height: 100vh; overflow: hidden; background: #f7f5f2; }
.pl-banner { position: relative; flex: none; height: 42vw; background: #ddd center/cover no-repeat; }
.pl-banner__veil {
  position: absolute; left: 0; right: 0; bottom: 0; height: 30%;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.16) 100%);
}
.pl-head {
  flex: none; position: relative; z-index: 2;
  margin-top: -40px; padding: 36px 32px 20px;
  background: #fff; border-radius: 40px 40px 0 0;
  box-shadow: 0 -8px 32px rgba(70,45,25,.10);
}
.pl-title-row { display: flex; align-items: center; gap: 16px; }
.pl-title-row h2 {
  margin: 0; font-size: 42px; line-height: 1.25; color: #1f1a17;
  font-weight: 700; letter-spacing: 1px;
  font-family: "Songti SC", "Noto Serif CJK SC", "STSong", serif;
}
.pl-live {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 4px 16px; font-size: 20px; font-weight: 600; letter-spacing: 1px;
  color: #d84a3f; background: rgba(216,74,63,.08);
  border: 1px solid rgba(216,74,63,.24); border-radius: 999px;
}
.pl-live i {
  width: 10px; height: 10px; border-radius: 50%; background: #e0503f;
  animation: plLivePulse 1.6s ease-out infinite;
}
@keyframes plLivePulse {
  0% { box-shadow: 0 0 0 0 rgba(224,80,63,.45); }
  70% { box-shadow: 0 0 0 16px rgba(224,80,63,0); }
  100% { box-shadow: 0 0 0 0 rgba(224,80,63,0); }
}
.pl-ended {
  padding: 4px 16px; font-size: 20px; color: #8f857c;
  background: #f4f0ec; border-radius: 999px;
}
.pl-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 14px 0 0; color: #9c9188; font-size: 24px; }
.pl-meta__sep { color: #d8cfc6; }
.pl-tabs { display: flex; align-items: center; justify-content: space-between; margin-top: 26px; }
.pl-tabs__group { display: flex; padding: 4px; background: #f5f1ec; border-radius: 999px; }
.pl-tab {
  padding: 12px 30px; font-size: 26px; color: #8f857c; border-radius: 999px;
  transition: color .2s, background .2s, box-shadow .2s;
}
.pl-tab.on { background: #fff; color: #1f1a17; font-weight: 600; box-shadow: 0 6px 16px rgba(70,45,25,.10); }
.pl-order {
  padding: 10px 22px; font-size: 24px; color: #9c9188; background: #fff;
  border: 1px solid #eee8e1; border-radius: 999px;
}
.pl-newtip {
  flex: none; align-self: center; margin-top: 16px; padding: 10px 26px;
  font-size: 24px; color: #a97c46; border-radius: 999px;
  background: rgba(255,255,255,.96); box-shadow: 0 8px 24px rgba(70,45,25,.12);
}
.pl-waterfall {
  position: relative; flex: 1 1 auto; min-height: 0;
  display: flex; gap: 20px; padding: 20px 24px calc(160px + env(safe-area-inset-bottom));
  overflow-y: auto; align-items: flex-start;
}
.pl-col { flex: 1; display: flex; flex-direction: column; gap: 20px; }
.pl-card {
  background: #fff; border-radius: 24px; overflow: hidden; position: relative;
  box-shadow: 0 8px 24px rgba(70,45,25,.08);
  transition: transform .18s ease, box-shadow .18s ease;
}
.pl-card:active { transform: scale(.985); }
.pl-card.enter { animation: plIn .28s ease; }
.pl-card.selected { outline: 4px solid #c7a06a; }
@keyframes plIn { from { opacity: 0; transform: translateY(-24px); } to { opacity: 1; transform: none; } }
.pl-empty { position: absolute; left: 0; right: 0; top: 0; padding: 20px 24px 0; pointer-events: none; }
.pl-sk { display: flex; gap: 20px; }
.pl-sk__col { flex: 1; display: flex; flex-direction: column; gap: 20px; }
.pl-sk__card { position: relative; height: 0; overflow: hidden; border-radius: 24px; background: #efe8e1; }
.pl-sk__card::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(100deg, rgba(255,255,255,0) 15%, rgba(255,255,255,.75) 50%, rgba(255,255,255,0) 85%);
  transform: translateX(-100%);
  animation: plSkShine 1.9s ease-in-out infinite;
}
.pl-sk__col:nth-child(2) .pl-sk__card::after { animation-delay: .5s; }
.pl-sk__hint {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  margin: 44px 0 0; color: #b1a79d; font-size: 24px; letter-spacing: 1px;
}
.pl-sk__hint i { width: 14px; height: 14px; border-radius: 50%; background: #c7a06a; animation: plSkPulse 1.6s ease-in-out infinite; }
@keyframes plSkShine { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
@keyframes plSkPulse { 0%, 100% { opacity: .25; transform: scale(.8); } 50% { opacity: 1; transform: scale(1); } }
.pl-loading--more {
  position: absolute; left: 0; right: 0; bottom: calc(160px + env(safe-area-inset-bottom));
  padding: 0; font-size: 24px; color: #b1a79d; text-align: center;
}
.pl-ph { position: relative; width: 100%; background: #efe8e1; overflow: hidden; }
.pl-ph__shine {
  position: absolute; left: 0; top: 0; width: 100%; height: 100%;
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.72) 50%, rgba(255,255,255,0) 100%);
  animation: plShine 1.15s ease-in-out infinite;
}
.pl-ph.ready .pl-ph__shine { display: none; }
.pl-ph img {
  position: absolute; left: 0; top: 0; width: 100%; height: 100%; object-fit: cover;
  opacity: 0; transition: opacity .28s ease;
}
.pl-ph img.show { opacity: 1; }
.pl-check {
  position: absolute; right: 16px; top: 16px; width: 44px; height: 44px;
  background: #c7a06a; color: #fff; border-radius: 50%; font-style: normal;
  font-size: 26px; line-height: 44px; text-align: center;
  box-shadow: 0 4px 12px rgba(80,50,20,.3);
}
@keyframes plShine {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
.pl-bar {
  position: fixed; left: 24px; right: 24px; bottom: calc(20px + env(safe-area-inset-bottom)); z-index: 6;
  display: flex; gap: 10px; padding: 12px;
  background: rgba(255,255,255,.86);
  -webkit-backdrop-filter: blur(20px); backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,.7); border-radius: 999px;
  box-shadow: 0 16px 40px rgba(60,40,20,.18);
}
.pl-bar button {
  flex: 1; height: 88px; border: 0; border-radius: 999px;
  background: #f5f1ec; color: #6d5f52; font-size: 24px; padding: 0 6px;
  transition: background .2s, color .2s;
}
.pl-bar button.on { background: #fff; color: #1f1a17; box-shadow: inset 0 0 0 2px #dcc3a2; }
.pl-bar button:disabled { opacity: .4; }
.pl-fab {
  position: fixed; right: 40px; bottom: calc(170px + env(safe-area-inset-bottom)); z-index: 5;
  display: flex; align-items: center; justify-content: center;
  width: 104px; height: 104px; border-radius: 50%;
  font-size: 24px; letter-spacing: 1px; color: #fff;
  background: linear-gradient(135deg, #cbab7d, #a97f4e);
  border: 2px solid rgba(255,255,255,.65);
  box-shadow: 0 12px 28px rgba(120,85,45,.32);
}
.pl-gallery {
  position: fixed; inset: 0; z-index: 40; background: rgba(0,0,0,.92);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
}
.pl-gallery__close {
  position: absolute; top: 32px; right: 32px; width: 80px; height: 80px;
  border: 0; border-radius: 50%; background: rgba(255,255,255,.15); color: #fff; font-size: 56px;
}
.pl-gallery__img { max-width: 100%; max-height: 72vh; object-fit: contain; }
.pl-gallery__actions {
  margin-top: 32px; display: flex; gap: 20px; align-items: center; color: #fff; font-size: 28px;
}
.pl-gallery__actions button {
  border: 0; border-radius: 32px; padding: 16px 28px; background: #c7a06a; color: #fff; font-size: 28px;
}
.pl-gallery__hint { color: rgba(255,255,255,.55); font-size: 24px; margin-top: 20px; }
.pl-share-mask {
  position: fixed; inset: 0; z-index: 30; background: rgba(0,0,0,.45);
  display: flex; align-items: flex-end; justify-content: center;
}
.pl-share {
  width: 100%; max-width: 680px; background: #fff; border-radius: 32px 32px 0 0;
  padding: 40px 32px calc(40px + env(safe-area-inset-bottom)); text-align: center;
}
.pl-share h3 { margin: 0 0 28px; font-size: 34px; }
.pl-share button {
  display: block; width: 100%; height: 88px; margin-top: 16px; border: 0; border-radius: 44px;
  background: #f5f1ec; color: #6d5f52; font-size: 30px;
}
.pl-share-cancel { background: transparent !important; color: #999 !important; }
.pl-poster { width: 70%; margin-top: 24px; border-radius: 16px; }
.pl-share-tip { font-size: 24px; color: #999; margin: 16px 0 0; }
</style>
